import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  useAnnulerMaDemande,
  useArrivee,
  useCreerUnCours,
  useDemanderUnCours,
  useRepondreAUneDemande,
  useTerminerUnCoursDepuisLArrivee,
} from '../features/arrivee/arrivee';
import { lireLErreur } from '../shared/api/client';
import { Bandeau } from '../shared/ui/Bandeau';
import { Bouton } from '../shared/ui/Bouton';
import { CaseACocher } from '../shared/ui/CaseACocher';
import { Carte } from '../shared/ui/Carte';
import { Saisie } from '../shared/ui/Champ';
import { Selection } from '../shared/ui/Selection';
import { ZoneDeTexte } from '../shared/ui/ZoneDeTexte';
import { EtatErreur } from '../shared/ui/EtatErreur';
import { EtatVide } from '../shared/ui/EtatVide';
import { LigneDePersonne } from '../shared/ui/LigneDePersonne';
import { Squelette } from '../shared/ui/Squelette';
import { Statut } from '../shared/ui/Statut';
import { Titre } from '../shared/ui/Titre';
import { nomAffiche } from '../shared/format/designation';

/**
 * **L'écran d'arrivée — `CDC-4` F-48 et F-49.**
 *
 * **CETTE PAGE COMPOSE, ELLE NE FABRIQUE PAS.** Règle 2 de `design-system-first` : « pas
 * de markup stylé dans une page ». Ce qui reste ici est **la composition et le layout
 * propre à cette page**, ce que la même règle autorise explicitement.
 *
 * **LES TROIS ÉTATS SONT TRAITÉS**, et `web/CLAUDE.md` en fait une condition d'achèvement :
 * « chargement par squelette, erreur avec réessai, et vide avec un message utile. **Une
 * page qui n'en traite qu'un est inachevée.** »
 *
 * **DEUX ÉCRANS VIDES SE RESSEMBLENT ET N'EN SONT PAS UN SEUL** (F-54) : « l'espace d'une
 * personne sans rattachement dit **aucun cours POSSIBLE**, les trois autres disent *aucun
 * cours*. **Ce sont deux écrans différents sous une même apparence**, et l'étape de
 * maquettage doit les distinguer. » C'est `accesPerdu` qui les sépare, et cette page les
 * distingue par leur texte.
 *
 * **AUCUN TOTAL DE TEMPS DE COURS ICI**, des deux côtés : « le total ne s'affiche nulle
 * part ailleurs — deux emplacements pour une même valeur finiraient par en montrer deux ».
 */
export function PageArrivee() {
  const arrivee = useArrivee();

  return (
    <main className="mx-auto flex w-full max-w-espace flex-col gap-6 p-6">
      {arrivee.isLoading && <Squelette lignes={5} />}

      {arrivee.isError && (
        <EtatErreur
          message={lireLErreur(arrivee.error).message}
          onReessayer={() => void arrivee.refetch()}
        />
      )}

      {arrivee.data !== undefined && (
        <>
          {arrivee.data.accesPerdu && (
            // « Une personne sans rattachement actif atteint encore son historique et
            // son espace de fichiers, et rien d'autre. » Le bandeau le dit, et il ne
            // ferme rien : « les lui fermer rendrait inatteignables des données que le
            // produit garde exprès ».
            <Bandeau ton="attention">
              Vous n’êtes plus rattaché à aucune structure. Vous gardez votre
              historique et vos fichiers ; aucun cours n’est possible tant
              qu’une structure ne vous rattache pas.
            </Bandeau>
          )}

          {arrivee.data.role === 'eleve' ? (
            <VueEleve arrivee={arrivee.data} />
          ) : (
            <VueTuteur arrivee={arrivee.data} />
          )}
        </>
      )}
    </main>
  );
}

/**
 * **Le chemin d'entrée dans un cours, PERMANENT tant que le cours vit** — F-48.
 *
 * « Un cours en train de se dérouler porte un bouton pour y entrer, **chemin permanent
 * tant que le cours vit — le lien d'auto-connexion étant consommé**. » Un lien qui ne
 * vaut qu'une fois ne peut donc pas être ce chemin, et c'est pour cela qu'il en existe
 * un second.
 */
function useAllerAuCours() {
  const navigate = useNavigate();
  return (coursId: string) => navigate(`/cours/${coursId}`);
}

type Arrivee = NonNullable<ReturnType<typeof useArrivee>['data']>;
type ArriveeEleve = Extract<Arrivee, { role: 'eleve' }>;
type ArriveeTuteur = Extract<Arrivee, { role: 'tuteur' }>;

/** **F-48 — trois blocs, liste fermée.** */
function VueEleve({ arrivee }: { readonly arrivee: ArriveeEleve }) {
  const allerAuCours = useAllerAuCours();
  const annulation = useAnnulerMaDemande();
  // **La ligne du tuteur ouvre son formulaire, et un seul à la fois** : « une seule
  // demande en attente à la fois, tous fils confondus », donc en ouvrir deux montrerait
  // deux chemins dont un seul aboutira.
  const [demandeA, poserDemandeA] = useState<string | null>(null);
  return (
    <>
      <section className="flex flex-col gap-3">
        <Titre niveau={1}>Mes cours</Titre>
        {arrivee.coursEnCours.length === 0 ? (
          <EtatVide titre="Aucun cours en train de se dérouler">
            {arrivee.accesPerdu
              ? 'Aucun cours n’est possible : vous n’êtes rattaché à aucune structure.'
              : 'Quand un de vos tuteurs ouvre un cours, il apparaît ici et vous pouvez y entrer.'}
          </EtatVide>
        ) : (
          <ul className="flex flex-col gap-3">
            {arrivee.coursEnCours.map((cours) => (
              <li key={cours.coursId}>
                <Carte>
                  <span className="flex items-center justify-between gap-4">
                    <span className="flex flex-col gap-1">
                      <Titre niveau={2} taille="18">
                        {cours.titre}
                      </Titre>
                      <Statut teinte="positif">
                        {`${cours.participantsPresents.length} présent${cours.participantsPresents.length > 1 ? 's' : ''}`}
                      </Statut>
                    </span>
                    <Bouton onClick={() => void allerAuCours(cours.coursId)}>
                      Entrer
                    </Bouton>
                  </span>
                </Carte>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <Titre niveau={2}>Mes tuteurs</Titre>
        {arrivee.demandeEnAttente !== null && (
          // « L'écran DIT qu'une demande est en attente, et le fil porte son état
          // détaillé. Il ne duplique pas la conversation, et il ne laisse pas l'élève
          // aveugle sur le fait qu'il a demandé. »
          <Bandeau
            ton="accent"
            // **« L'élève annule et renvoie autant de fois qu'il le veut »** (F-14). Sans
            // ce geste, le plafond d'une seule demande est un mur : il faudrait attendre
            // quinze minutes l'expiration pour en adresser une autre. Même forme que le
            // plafond de deux cours du tuteur, et le corpus la ferme de la même façon —
            // par un geste, sur l'écran qui montre le blocage.
            action={
              <Bouton
                variante="douce"
                taille="petite"
                enCours={annulation.isPending}
                onClick={() => {
                  annulation.mutate(arrivee.demandeEnAttente?.demandeId ?? '');
                }}
              >
                Annuler
              </Bouton>
            }
          >
            {`Votre demande à ${arrivee.demandeEnAttente.tuteurPrenom ?? 'votre tuteur'} attend une réponse.`}
          </Bandeau>
        )}
        {annulation.isError && (
          <Bandeau ton="echec">{lireLErreur(annulation.error).message}</Bandeau>
        )}
        {arrivee.mesTuteurs.length === 0 ? (
          <EtatVide titre="Aucune relation">
            Une structure vous met en relation avec un tuteur. Sans relation,
            aucun cours et aucune conversation ne sont possibles.
          </EtatVide>
        ) : (
          <Carte>
            <ul className="flex flex-col">
              {arrivee.mesTuteurs.map((tuteur) => (
                <LigneDePersonne
                  key={tuteur.id}
                  prenom={tuteur.prenom}
                  etat={tuteur.etat}
                  action={
                    // « La demande de cours part de cet écran, depuis la ligne d'un
                    // tuteur, **y compris s'il est occupé** » — c'est son second usage.
                    // Le bouton n'est donc PAS grisé sur un tuteur occupé.
                    //
                    // **Il l'est sans conversation accessible**, et c'est une règle et non
                    // une prudence : la demande est « rattachée à la conversation où elle
                    // vit », et « recréer une relation ne rouvre pas l'ancien fil ». Sans
                    // fil, il n'y a rien à quoi la rattacher.
                    <Bouton
                      variante="faible"
                      taille="petite"
                      disabled={
                        arrivee.demandeEnAttente !== null ||
                        tuteur.conversationId === null
                      }
                      onClick={() =>
                        poserDemandeA(
                          demandeA === tuteur.id ? null : tuteur.id,
                        )
                      }
                    >
                      Demander un cours
                    </Bouton>
                  }
                />
              ))}
            </ul>
          </Carte>
        )}

        {/* **Le formulaire de la demande, sous la liste et non dans la ligne** : ses deux
            champs sont du texte long — « aucune longueur maximum n'est une règle du
            produit » —, et les serrer dans une ligne de liste les rendrait inutilisables. */}
        {demandeA !== null && (
          <FormulaireDeDemande
            tuteur={
              arrivee.mesTuteurs.find((t) => t.id === demandeA) ?? null
            }
            onFini={() => poserDemandeA(null)}
          />
        )}
      </section>

      <DerniersCours cours={arrivee.derniersCours} />
    </>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE FORMULAIRE D'UNE DEMANDE DE COURS — DEUX CHAMPS, ET LE SECOND EST FACULTATIF.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-14, portée écrite par **Q555** : « la demande porte **deux champs de texte** : **ce que
 * l'élève veut travailler**, **obligatoire** ; et **ce qu'il n'arrive pas à faire**,
 * **facultatif**. Le premier obligatoire et le second facultatif **est le patron du
 * cours**, qui porte un titre obligatoire et une description facultative. »
 *
 * **AUCUNE LONGUEUR MAXIMUM**, et c'est écrit : « aucune longueur maximum, patron du
 * pense-bête ». Le front n'en impose donc aucune — ni compteur, ni troncature.
 *
 * **CE QUE CES DEUX CHAMPS SONT, ET POURQUOI L'ÉCRAN NE LES TRAITE PAS COMME DU TEXTE
 * ORDINAIRE.** `MODELE-DONNEES.md` §3.3 les classe **au niveau le plus haut sur toute la
 * colonne**, et F-14 dit pourquoi : « ce sont **les seules colonnes du produit écrites par un
 * élève sur ce qu'il n'arrive pas à faire** ». Ils restent « fermés à la structure et au
 * compte qui voit tout », la demande vivant dans le chat.
 *
 * **La conséquence à l'écran est une phrase, et elle vaut mieux qu'un silence** : l'aide dit
 * qui lira. Un élève qui écrit ce qu'il ne comprend pas a le droit de savoir que son
 * établissement ne le lit pas.
 */
function FormulaireDeDemande({
  tuteur,
  onFini,
}: {
  readonly tuteur: {
    readonly id: string;
    readonly prenom: string | null;
    readonly conversationId: string | null;
  } | null;
  readonly onFini: () => void;
}) {
  const demande = useDemanderUnCours();
  const [sujet, poserSujet] = useState('');
  const [blocage, poserBlocage] = useState('');

  if (tuteur === null || tuteur.conversationId === null) return null;
  const conversationId = tuteur.conversationId;

  return (
    <Carte>
      <form
        className="flex flex-col gap-3"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          // **Le sujet est obligatoire côté serveur** — critère 5 de F-14, refus
          // `DEMANDE_SUJET_MANQUANT`. Le front ne double pas cette règle par une
          // validation à lui : il évite seulement d'envoyer un appel dont on sait qu'il
          // sera refusé, et c'est le serveur qui décide.
          if (sujet.trim() === '') return;
          demande.mutate(
            {
              tuteurId: tuteur.id,
              conversationId,
              sujet: sujet.trim(),
              blocage: blocage.trim(),
            },
            { onSuccess: onFini },
          );
        }}
      >
        <Titre niveau={3} taille="18">
          {`Demander un cours à ${tuteur.prenom ?? 'ce tuteur'}`}
        </Titre>
        <ZoneDeTexte
          libelle="Ce que vous voulez travailler"
          aide="Votre tuteur le lit avant de répondre. Votre établissement, non."
          value={sujet}
          onChange={(evenement) => poserSujet(evenement.target.value)}
        />
        <ZoneDeTexte
          libelle="Ce que vous n’arrivez pas à faire"
          aide="Facultatif."
          value={blocage}
          onChange={(evenement) => poserBlocage(evenement.target.value)}
        />
        {demande.isError && (
          <Bandeau ton="echec">{lireLErreur(demande.error).message}</Bandeau>
        )}
        <span className="flex gap-2">
          <Bouton type="submit" enCours={demande.isPending}>
            Envoyer la demande
          </Bouton>
          <Bouton variante="douce" onClick={onFini}>
            Annuler
          </Bouton>
        </span>
      </form>
    </Carte>
  );
}

/** **F-49 — quatre blocs, liste fermée.** */
function VueTuteur({ arrivee }: { readonly arrivee: ArriveeTuteur }) {
  const allerAuCours = useAllerAuCours();
  const fin = useTerminerUnCoursDepuisLArrivee();
  const [creationOuverte, poserCreationOuverte] = useState(false);
  return (
    <>
      <section className="flex flex-col gap-3">
        <span className="flex items-center justify-between gap-4">
          <Titre niveau={1}>Mes cours</Titre>
          {/* « **La création d'un cours part de cet écran.** » Le bouton bascule le
              formulaire ci-dessous, et il reste grisé au plafond — F-49 critère 1. */}
          <Bouton
            disabled={!arrivee.plafond.creationPossible}
            onClick={() => poserCreationOuverte(!creationOuverte)}
          >
            Créer un cours
          </Bouton>
        </span>

        {!arrivee.plafond.creationPossible && !arrivee.accesPerdu && (
          // « Au plafond de deux cours non terminés, le geste de création est refusé
          // avec sa raison, et **l'écran NOMME les cours qu'il peut terminer** pour
          // libérer une place — sinon le refus est un mur sans issue. » La liste
          // ci-dessous EST cette nomination.
          <Bandeau ton="attention">
            {`Vous tenez ${arrivee.plafond.detenus} cours non terminés sur ${arrivee.plafond.plafond}. Terminez-en un ci-dessous pour en créer un autre.`}
          </Bandeau>
        )}

        {creationOuverte && arrivee.plafond.creationPossible && (
          <FormulaireDeCreation
            eleves={arrivee.mesEleves}
            structures={arrivee.structuresPossibles}
            onFini={() => poserCreationOuverte(false)}
          />
        )}

        {fin.isError && (
          <Bandeau ton="echec">{lireLErreur(fin.error).message}</Bandeau>
        )}

        {arrivee.coursNonTermines.length === 0 ? (
          <EtatVide titre="Aucun cours en train de se dérouler">
            {arrivee.accesPerdu
              ? 'Aucun cours n’est possible : vous n’êtes rattaché à aucune structure.'
              : 'Créez un cours pour y faire entrer vos élèves.'}
          </EtatVide>
        ) : (
          <ul className="flex flex-col gap-3">
            {arrivee.coursNonTermines.map((cours) => (
              <li key={cours.coursId}>
                <Carte>
                  <span className="flex items-center justify-between gap-4">
                    <span className="flex flex-col gap-1">
                      <Titre niveau={2} taille="18">
                        {cours.titre}
                      </Titre>
                      {/* « Le bloc des cours montre, par cours, les participants
                          PRÉSENTS — et PAS les liens consommés : un lien consommé ne
                          dit pas que la personne arrive, elle a pu fermer l'onglet. » */}
                      <span className="text-14 text-texte-faible">
                        {arrivee.coursNonTermines.length === 0
                          ? ''
                          : cours.participantsPresents
                              .map((p) => nomAffiche(p.prenom))
                              .join(', ') || 'Personne n’est encore entré'}
                      </span>
                    </span>
                    <span className="flex gap-2">
                      {/* **Terminer depuis ICI, et c'est un second chemin assumé** — le
                          premier est dans le tableau. F-49 l'exige : l'écran « NOMME les
                          cours qu'il peut terminer pour libérer une place, **sinon le refus
                          est un mur sans issue** ». Nommer sans permettre de terminer
                          laisserait le mur. Les deux chemins appellent la MÊME route, donc
                          il n'y a pas deux autorisations à tenir d'accord. */}
                      <Bouton
                        variante="douce"
                        enCours={fin.isPending}
                        onClick={() => {
                          fin.mutate(cours.coursId);
                        }}
                      >
                        Terminer
                      </Bouton>
                      <Bouton onClick={() => void allerAuCours(cours.coursId)}>
                        Entrer
                      </Bouton>
                    </span>
                  </span>
                </Carte>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <Titre niveau={2}>Demandes reçues</Titre>
        {arrivee.demandesRecues.length === 0 ? (
          <EtatVide titre="Aucune demande en attente">
            Quand un élève vous propose un cours, sa demande apparaît ici avec
            ce qu’il veut travailler.
          </EtatVide>
        ) : (
          <ul className="flex flex-col gap-3">
            {arrivee.demandesRecues.map((demande) => (
              <li key={demande.demandeId}>
                <Carte>
                  <span className="flex flex-col gap-3">
                    <Titre niveau={3} taille="18">
                      {nomAffiche(demande.elevePrenom)}
                    </Titre>
                    {/* **LES DEUX TEXTES SONT MONTRÉS ICI**, Q555 : « il accepte ou
                        refuse depuis cet écran comme depuis le fil, et **décider sans
                        lire ce sur quoi on décide n'est pas un geste que le produit
                        doit rendre possible**. » */}
                    <p className="text-16 leading-normal text-texte">
                      {demande.sujet}
                    </p>
                    {demande.difficulte !== null && (
                      <p className="text-14 leading-normal text-texte-faible">
                        {demande.difficulte}
                      </p>
                    )}
                    <ReponseALaDemande
                      demandeId={demande.demandeId}
                      coursOuverts={arrivee.coursNonTermines}
                      creationPossible={arrivee.plafond.creationPossible}
                    />
                  </span>
                </Carte>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-3">
        <Titre niveau={2}>Mes élèves</Titre>
        {arrivee.mesEleves.length === 0 ? (
          <EtatVide titre="Aucune relation">
            Une structure vous met en relation avec vos élèves. Sans relation,
            aucun cours et aucune conversation ne sont possibles.
          </EtatVide>
        ) : (
          <Carte>
            <ul className="flex flex-col">
              {arrivee.mesEleves.map((eleve) => (
                <LigneDePersonne
                  key={eleve.id}
                  prenom={eleve.prenom}
                  etat={eleve.etat}
                />
              ))}
            </ul>
          </Carte>
        )}
      </section>

      <DerniersCours cours={arrivee.derniersCours} />
    </>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LE FORMULAIRE DE CRÉATION D'UN COURS — F-49, ET LE SÉLECTEUR DE STRUCTURE N'Y
 * PARAÎT QUE SOUS CONDITION.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « La création d'un cours part de cet écran, et **le sélecteur de structure n'apparaît que
 * s'il a deux rattachements**. »
 *
 * **CETTE CONDITION EST UNE RÈGLE, PAS UN CONFORT.** Un tuteur qui n'a qu'un rattachement n'a
 * rien à choisir : lui montrer une liste d'un élément l'obligerait à décider de ce que le
 * produit sait déjà. Et le contrat le dit de son côté — `organizationId` est « à omettre
 * quand le tuteur n'a qu'un rattachement ».
 *
 * **Ce sont les rattachements ACTIFS qui comptent**, jamais les terminés : la route les rend
 * « actifs ET terminés, avec leurs dates », et un rattachement clos ne porte aucun cours neuf.
 *
 * **UN COURS SANS ÉLÈVE N'EST PAS ENVOYÉ**, et c'est le serveur qui tient la règle : « une
 * relation avec le tuteur est le préalable pour chacun ». Le front ne double pas cette
 * autorisation — il évite un appel dont le refus est certain, et le plafond d'élèves par
 * cours reste **entièrement** au serveur, qui est le seul à connaître le réglage de la
 * structure.
 */
function FormulaireDeCreation({
  eleves,
  structures,
  onFini,
}: {
  readonly eleves: readonly { id: string; prenom: string | null }[];
  readonly structures: readonly { id: string; nom: string }[];
  readonly onFini: () => void;
}) {
  const creation = useCreerUnCours();
  const [titre, poserTitre] = useState('');
  const [description, poserDescription] = useState('');
  const [choisis, poserChoisis] = useState<readonly string[]>([]);
  const [structure, poserStructure] = useState('');

  // « Le sélecteur n'apparaît que s'il a DEUX rattachements » — le front COMPTE, il ne
  // déduit rien : la liste ne porte que les rattachements actifs, et c'est le serveur qui
  // l'a bornée.
  const doitChoisir = structures.length > 1;

  return (
    <Carte>
      <form
        className="flex flex-col gap-3"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          if (titre.trim() === '' || choisis.length === 0) return;
          if (doitChoisir && structure === '') return;
          creation.mutate(
            {
              titre: titre.trim(),
              description: description.trim(),
              elevesIds: [...choisis],
              // **Omis quand il n'y a rien à choisir**, ce que le contrat demande.
              ...(doitChoisir ? { organizationId: structure } : {}),
            },
            { onSuccess: onFini },
          );
        }}
      >
        <Titre niveau={3} taille="18">
          Nouveau cours
        </Titre>

        <Saisie
          libelle="Titre du cours"
          value={titre}
          onChange={(evenement) => poserTitre(evenement.target.value)}
        />
        <ZoneDeTexte
          libelle="Description"
          aide="Elle suit le cloisonnement du cours."
          value={description}
          onChange={(evenement) => poserDescription(evenement.target.value)}
        />

        {doitChoisir && (
          <Selection
            libelle="Structure qui porte le cours"
            aide="Vous avez plusieurs rattachements actifs : choisissez celui de ce cours."
            options={[
              { valeur: '', libelle: '—' },
              ...structures.map((ligne) => ({
                valeur: ligne.id,
                libelle: ligne.nom,
              })),
            ]}
            value={structure}
            onChange={(evenement) => poserStructure(evenement.target.value)}
          />
        )}

        {eleves.length === 0 ? (
          // « Sans relation, aucun cours n'est possible » : le dire ici évite un
          // formulaire qu'on remplit pour rien.
          <Bandeau ton="attention">
            Vous n’avez aucun élève en relation. Une structure vous en met en
            relation ; sans cela, aucun cours n’est possible.
          </Bandeau>
        ) : (
          <span className="flex flex-col gap-1">
            <span className="text-14 font-weight-700 text-texte-fort">
              Élèves du cours
            </span>
            {eleves.map((eleve) => (
              <CaseACocher
                key={eleve.id}
                libelle={nomAffiche(eleve.prenom)}
                coche={choisis.includes(eleve.id)}
                onChanger={(coche) =>
                  poserChoisis(
                    coche
                      ? [...choisis, eleve.id]
                      : choisis.filter((id) => id !== eleve.id),
                  )
                }
              />
            ))}
          </span>
        )}

        {creation.isError && (
          <Bandeau ton="echec">{lireLErreur(creation.error).message}</Bandeau>
        )}

        <span className="flex gap-2">
          <Bouton type="submit" enCours={creation.isPending}>
            Créer le cours
          </Bouton>
          <Bouton variante="douce" onClick={onFini}>
            Annuler
          </Bouton>
        </span>
      </form>
    </Carte>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **ACCEPTER OU REFUSER — ET L'ACCEPTATION A DEUX FORMES, PAR RÈGLE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Q298 : « **le même chemin sert à proposer un cours neuf et à demander à rejoindre un cours
 * déjà commencé**. » Le tuteur choisit donc, en acceptant : un cours ouvert de sa liste, ou
 * un cours neuf.
 *
 * **LE TITRE NE SE PRÉREMPLIT PAS DEPUIS LA DEMANDE, ET C'EST UN CRITÈRE D'ACCEPTATION.**
 * F-14 critère 6 : « le système DOIT créer le cours **avec le titre et la description que le
 * tuteur saisit**, et **ne recopier ni l'un ni l'autre des deux champs de la demande** ». Le
 * motif est écrit : « ces deux champs appartiennent à la demande, et à rien d'autre. **Un
 * tuteur qui accepte lit la demande et ouvre son cours ; il ne l'hérite pas.** »
 *
 * **La tentation est forte, et c'est pour cela que le critère existe** : le sujet de l'élève
 * ferait un titre commode. Il ferait aussi entrer, dans le titre d'un cours — que lisent un
 * `orgadmin` et le compte qui voit tout —, un texte classé **au niveau le plus haut** et que
 * F-49 garde « fermé à la structure et au compte qui voit tout ». **Le raccourci d'écran
 * serait une fuite de cloisonnement.**
 *
 * **LE REFUS EST SEC** : « aucun champ de motif n'existe », et il n'y en a aucun ici. Il part
 * d'un clic, sans formulaire — un formulaire de refus finirait par porter un champ.
 */
function ReponseALaDemande({
  demandeId,
  coursOuverts,
  creationPossible,
}: {
  readonly demandeId: string;
  readonly coursOuverts: readonly { coursId: string; titre: string }[];
  readonly creationPossible: boolean;
}) {
  const reponse = useRepondreAUneDemande();
  const [acceptation, poserAcceptation] = useState(false);
  const [coursExistant, poserCoursExistant] = useState('');
  const [titre, poserTitre] = useState('');
  const [description, poserDescription] = useState('');

  if (!acceptation) {
    return (
      <span className="flex flex-col gap-2">
        <span className="flex gap-2">
          <Bouton onClick={() => poserAcceptation(true)}>Accepter</Bouton>
          <Bouton
            variante="douce"
            enCours={reponse.isPending}
            onClick={() => {
              reponse.mutate({ demandeId, accepte: false });
            }}
          >
            Refuser
          </Bouton>
        </span>
        {reponse.isError && (
          <Bandeau ton="echec">{lireLErreur(reponse.error).message}</Bandeau>
        )}
      </span>
    );
  }

  const versUnCoursNeuf = coursExistant === '';

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        if (versUnCoursNeuf && titre.trim() === '') return;
        reponse.mutate({
          demandeId,
          accepte: true,
          ...(versUnCoursNeuf
            ? {
                titre: titre.trim(),
                description: description.trim(),
              }
            : { coursId: coursExistant }),
        });
      }}
    >
      {coursOuverts.length > 0 && (
        <Selection
          libelle="Où entre cet élève"
          aide="Un de vos cours ouverts, ou un cours neuf."
          options={[
            { valeur: '', libelle: 'Un cours neuf' },
            ...coursOuverts.map((cours) => ({
              valeur: cours.coursId,
              libelle: cours.titre,
            })),
          ]}
          value={coursExistant}
          onChange={(evenement) => poserCoursExistant(evenement.target.value)}
        />
      )}

      {versUnCoursNeuf && (
        <>
          {!creationPossible && (
            // Le plafond de deux cours non terminés s'applique aussi par ce chemin : un
            // cours neuf en est un. Le dire ici évite un refus qui semblerait venir de
            // la demande.
            <Bandeau ton="attention">
              Vous êtes au plafond de cours non terminés. Terminez-en un, ou
              faites entrer cet élève dans un cours ouvert.
            </Bandeau>
          )}
          {/* **Vides, et jamais préremplis depuis la demande** — critère 6 de F-14. */}
          <Saisie
            libelle="Titre du cours"
            value={titre}
            onChange={(evenement) => poserTitre(evenement.target.value)}
          />
          <ZoneDeTexte
            libelle="Description"
            value={description}
            onChange={(evenement) => poserDescription(evenement.target.value)}
          />
        </>
      )}

      {reponse.isError && (
        <Bandeau ton="echec">{lireLErreur(reponse.error).message}</Bandeau>
      )}

      <span className="flex gap-2">
        <Bouton type="submit" enCours={reponse.isPending}>
          Accepter la demande
        </Bouton>
        <Bouton variante="douce" onClick={() => poserAcceptation(false)}>
          Revenir
        </Bouton>
      </span>
    </form>
  );
}

/**
 * **« Ses derniers cours, avec un chemin vers l'historique complet. »**
 *
 * Le même bloc des deux côtés, et le chemin est ce qui fait que **cet écran n'est pas
 * l'historique** (Q482) : la liste complète, ses deux durées et ses totaux vivent ailleurs.
 */
function DerniersCours({
  cours,
}: {
  readonly cours: readonly { coursId: string; titre: string; debut: string }[];
}) {
  if (cours.length === 0) {
    return (
      <EtatVide titre="Aucun cours passé">
        Vos cours terminés se retrouvent ici, et dans votre historique complet.
      </EtatVide>
    );
  }
  return (
    <section className="flex flex-col gap-3">
      <Titre niveau={2}>Derniers cours</Titre>
      <Carte>
        <ul className="flex flex-col">
          {cours.map((ligne) => (
            <li
              key={ligne.coursId}
              className="flex min-h-cible items-center justify-between gap-4 border-b border-bord py-2 last:border-b-0"
            >
              <span className="text-16 text-texte-fort">{ligne.titre}</span>
              <time
                dateTime={ligne.debut}
                className="text-13 text-texte-faible"
              >
                {new Date(ligne.debut).toLocaleDateString('fr-FR')}
              </time>
            </li>
          ))}
        </ul>
      </Carte>
    </section>
  );
}
